import { Link } from "react-router-dom";
import { AppleLogo, GooglePlayLogo } from "@phosphor-icons/react";
import { Logo } from "@/components/site/Logo";
import { SECTIONS, usePageNav } from "@/components/site/nav";
import { STORE_LINKS, SUPPORT_EMAIL, isLiveLink } from "@/config/links";
import { useStoreCTA } from "@/components/site/ComingSoon";

const legalLinks = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/refund", label: "Refund Policy" },
  { to: "/cookie", label: "Cookie Notice" },
];

export const Footer = () => {
  const { goToSection, goTop } = usePageNav();
  const { open: openComingSoon } = useStoreCTA();
  const storeClick = (url) => (e) => {
    if (!isLiveLink(url)) {
      e.preventDefault();
      openComingSoon();
    }
  };

  return (
    <footer
      className="relative z-10 border-t border-white/8 bg-[#04060d] px-4 pt-16 pb-8 sm:px-6"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/55">
              An AI-powered cryptocurrency intelligence platform for analyzing
              markets, evaluating risk and monitoring assets with clarity.
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href={STORE_LINKS.appStore}
                onClick={storeClick(STORE_LINKS.appStore)}
                data-testid="footer-app-store"
                aria-label="Download on the App Store"
                className="grid h-11 w-11 place-items-center rounded-xl border border-white/12 text-white/80 transition-colors hover:border-teal-500/40 hover:text-teal-400"
              >
                <AppleLogo size={22} weight="fill" />
              </a>
              <a
                href={STORE_LINKS.googlePlay}
                onClick={storeClick(STORE_LINKS.googlePlay)}
                data-testid="footer-google-play"
                aria-label="Get it on Google Play"
                className="grid h-11 w-11 place-items-center rounded-xl border border-white/12 text-white/80 transition-colors hover:border-teal-500/40 hover:text-teal-400"
              >
                <GooglePlayLogo size={20} weight="fill" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              Product
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => goToSection(s.id)}
                    className="text-white/60 transition-colors hover:text-white"
                    data-testid={`footer-link-${s.id}`}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              Legal
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              {legalLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={goTop}
                    data-testid={`footer-${l.to.slice(1)}`}
                    className="text-white/60 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              Company
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <Link
                  to="/support"
                  onClick={goTop}
                  data-testid="footer-support"
                  className="text-white/60 transition-colors hover:text-white"
                >
                  Support &amp; Contact
                </Link>
              </li>
              <li>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-white/60 transition-colors hover:text-white"
                  data-testid="footer-email"
                >
                  {SUPPORT_EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 rounded-2xl border border-white/8 bg-white/[0.015] p-5">
          <p className="text-xs leading-relaxed text-white/45">
            <span className="font-semibold text-white/60">Disclaimer.</span>{" "}
            Velora Intelligence provides informational and analytical tools for
            cryptocurrency markets. It does not provide financial, investment,
            legal, or tax advice. Cryptocurrency investments involve substantial
            risk, including the possible loss of capital.
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-white/8 pt-6 text-center sm:flex-row sm:text-left">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Velora Intelligence. All rights reserved.
          </p>
          <p className="text-xs text-white/40">veloraintelligence.ai</p>
        </div>
      </div>
    </footer>
  );
};
